import React, { useCallback, useId, useMemo, useRef } from "react";

import { Box, Typography } from "@mui/material";

import { TalentExperienceData } from "@/cards/common/types";
import {
  pauseOtherTaasCardAudios,
  TAAS_CARD_AUDIO_ATTR,
} from "@/shared/components/audio/singleCardAudio";
import useThemeStore from "@/shared/stores/useThemeStore";

interface AudioDisplayProps {
  data?: TalentExperienceData;
}

const AudioDisplay: React.FC<AudioDisplayProps> = ({ data = {} }) => {
  const { darkMode } = useThemeStore();

  const audioRef = useRef<HTMLAudioElement>(null);

  const reactId = useId();

  const audioId = useMemo(() => {
    const url = data.audio?.audioUrl;

    if (!url) return `taas-audio${reactId.replace(/:/g, "")}`;

    let hash = 0;

    for (let i = 0; i < url.length; i += 1) {
      hash = (hash << 5) - hash + url.charCodeAt(i);

      hash |= 0;
    }

    return `taas-audio-${Math.abs(hash)}${reactId.replace(/:/g, "")}`;
  }, [data.audio?.audioUrl, reactId]);

  const handlePlay = useCallback(() => {
    const current = audioRef.current;

    if (current) pauseOtherTaasCardAudios(current);
  }, []);

  if (!data.audio) return null;

  const textSecondary = darkMode ? "var(--neutral-300)" : "var(--neutral-600)";

  return (
    <Box
      sx={{
        width: "100%",

        height: "100%",

        display: "flex",

        flexDirection: "column",

        alignItems: "center",

        justifyContent: "center",

        p: 4,

        gap: 3,
      }}
    >
      {data.audio.audioUrl && (
        <Box
          sx={{
            width: "100%",

            maxWidth: 400,
          }}
        >
          <audio
            ref={audioRef}
            id={audioId}
            {...{ [TAAS_CARD_AUDIO_ATTR]: "" }}
            controls
            src={data.audio.audioUrl}
            onPlay={handlePlay}
            style={{
              width: "100%",
            }}
          />
        </Box>
      )}

      {data.audio.description && (
        <Typography
          variant="body1"
          sx={{
            color: textSecondary,

            textAlign: "center",
          }}
        >
          {data.audio.description}
        </Typography>
      )}
    </Box>
  );
};

export default AudioDisplay;
